import {LinkOutlined} from '@ant-design/icons';
import type {Settings as LayoutSettings} from '@ant-design/pro-components';
import type {RequestConfig, RunTimeLayoutConfig} from '@umijs/max';
import {history, Link} from '@umijs/max';
import {dropByCacheKey} from '@@/plugin-keepalive';
import React from 'react';
import {AvatarDropdown, AvatarName, DutyButton, Footer, NoticeBell, SelectLang,} from '@/components';
import {currentUser as queryCurrentUser} from '@/services/login/api';
import defaultSettings from '../config/defaultSettings';
import {errorConfig} from './requestErrorConfig';
import '@ant-design/v5-patch-for-react-19';
import moment from 'moment';
import 'moment/locale/zh-cn';

moment.locale('zh-cn'); // 设置 moment.js 的全局语言为中文

const isDev = process.env.NODE_ENV === 'development';
const loginPath = '/user/login';

/**
 * @see https://umijs.org/docs/api/runtime-config#getinitialstate
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: UserAPI.CurrentUser;
  loading?: boolean;
  fetchUserInfo?: () => Promise<UserAPI.CurrentUser | undefined>;
}> {
  const fetchUserInfo = async () => {
    try {
      const msg = await queryCurrentUser({
        skipErrorHandler: true,
      });
      return msg.data;
    } catch (error) {
      const {location} = history;
      if (location.pathname !== loginPath) {
        history.push(`${loginPath}?redirect=${encodeURIComponent(location.pathname + location.search)}`);
      }
    }
    return undefined;
  };

  // 如果不是登录页面，执行
  const {location} = history;
  if (location.pathname !== loginPath) {
    const currentUser = await fetchUserInfo();
    return {
      fetchUserInfo,
      currentUser,
      settings: defaultSettings as Partial<LayoutSettings>,
    };
  }
  return {
    fetchUserInfo,
    settings: defaultSettings as Partial<LayoutSettings>,
  };

}

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({initialState}) => {
  return {
    actionsRender: () => [
      <NoticeBell key="notice"/>,
      <DutyButton key="duty"/>,
      <SelectLang key="SelectLang"/>,
    ],
    avatarProps: {
      src: initialState?.currentUser?.avatar,
      title: <AvatarName/>,
      render: (_, avatarChildren) => {
        return <AvatarDropdown>{avatarChildren}</AvatarDropdown>;
      },
    },
    waterMarkProps: {
      content: initialState?.currentUser?.name,
      fontColor: 'rgba(0,0,0,0.045)',
      gapX: 240,
      gapY: 240,
    },
    footerRender: () => <Footer/>,
    onPageChange: () => {
      const {location} = history;
      if (!initialState?.currentUser?.pid && location.pathname !== loginPath) {
        history.replace(`${loginPath}?redirect=${encodeURIComponent(location.pathname + location.search)}`);
      }
    },
    bgLayoutImgList: [
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/D2LWSqNny4sAAAAAAAAAAAAAFl94AQBr',
        left: 85,
        bottom: 100,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/C2TWRpJpiC0AAAAAAAAAAAAAFl94AQBr',
        bottom: -68,
        right: -45,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/F6vSTbj8KpYAAAAAAAAAAAAAFl94AQBr',
        bottom: 0,
        left: 0,
        width: '331px',
      },
    ],
    links: isDev
      ? [
        <Link key="openapi" to="/umi/plugin/openapi" target="_blank">
          <LinkOutlined/>
          <span>OpenAPI 文档</span>
        </Link>,
      ]
      : [],
    menuHeaderRender: undefined,
    ...initialState?.settings,
  };
};

/**
 * @name request 配置，可以配置错误处理
 * 它基于 axios 和 ahooks 的 useRequest 提供了一套统一的网络请求和错误处理方案。
 * @doc https://umijs.org/docs/max/request#配置
 */
export const request: RequestConfig = {
  timeout: 60000,
  ...errorConfig,
};

/**
 * @name 路由切换钩子
 * @description 已发布、已归档列表页加入了 keepalive 缓存（见 config.ts），使「查看详情 → 返回」
 *   回到列表时保留搜索条件、分页与滚动位置。但若以「其他方式」进入这些列表（菜单点击、
 *   直接访问、从别的页面跳转），则丢弃缓存强制重新挂载，等价于刷新页面拉取最新数据。
 *   判定标准：仅当上一个路径是该列表自身的 /detail/:id 详情页时才保留缓存，否则刷新。
 * @doc https://umijs.org/docs/api/runtime-config#onroutechange
 */
const KEEPALIVE_LIST_PATHS = [
  '/sign-manage/published',
  '/sign-auth/published',
  '/sign-manage/archived',
  '/sign-auth/archived',
];
let prevPathname = '';
export function onRouteChange({location}: {location: {pathname: string}}) {
  const pathname = location.pathname;
  const listPath = KEEPALIVE_LIST_PATHS.find((p) => p === pathname);
  if (listPath) {
    const isReturnFromDetail = prevPathname.startsWith(`${listPath}/detail/`);
    if (!isReturnFromDetail) {
      dropByCacheKey(listPath);
    }
  }
  prevPathname = pathname;
}
